import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { Icon } from './Icon';

/**
 * D1-F2b: banner de "modo soporte" en el panel del comercio.
 *
 * Se renderiza SOLO cuando GET /auth/me devuelve `impersonation` — es decir,
 * cuando un super-admin del SaaS entró al panel de un comercio en modo solo
 * lectura para diagnosticar. El banner avisa de forma visible para que nadie
 * confunda la sesión (ni el comerciante ni el propio super-admin).
 *
 * Es `position: fixed` arriba de todo y siempre visible — embebido en Whatsfy
 * o standalone, sin importar el scroll — porque la confusión de sesión es un
 * riesgo permanente en modo soporte.
 *
 * El alto del banner es variable: el texto envuelve en viewports estrechos
 * (mobile / iframe) o cuando se muestra un error. Por eso reportamos el alto
 * real al padre vía `onHeightChange` en lugar de hardcodear un padding —
 * el shell (Layout) usa ese valor para reservar espacio y no tapar contenido.
 */
export function SupportModeBanner({
  superAdmin,
  onHeightChange,
}: {
  superAdmin: string;
  onHeightChange?: (height: number) => void;
}) {
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  // Mide el alto real del banner y lo reporta al padre. ResizeObserver cubre
  // el reflow cuando el texto envuelve al cambiar el ancho del viewport o al
  // aparecer/desaparecer la línea de error.
  useEffect(() => {
    const el = bannerRef.current;
    if (!el || !onHeightChange) return;
    onHeightChange(el.getBoundingClientRect().height);
    const ro = new ResizeObserver(() => {
      onHeightChange(el.getBoundingClientRect().height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [onHeightChange]);

  const stopImpersonation = async () => {
    setStopping(true);
    setError(null);
    try {
      await api.post<{ ok: true }>('/api/super-admin/impersonate/stop');
    } catch (e) {
      // 401 = la cookie de soporte ya no es válida; el objetivo (terminar la
      // sesión) igual se cumple, así que seguimos al reload. Cualquier otro
      // error sí lo mostramos y dejamos reintentar.
      if (!(e instanceof ApiError) || e.status !== 401) {
        setError(
          e instanceof Error
            ? e.message
            : 'No pudimos terminar la sesión de soporte.',
        );
        setStopping(false);
        return;
      }
    }
    // La sesión de soporte quedó sin cookie — el siguiente /me dará 401.
    // Recargamos en la raíz para volver a un estado de sesión limpio.
    window.location.href = '/';
  };

  return (
    <div
      ref={bannerRef}
      role="status"
      className="banner banner-warn support-mode-banner"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 200,
        borderRadius: 0,
        borderLeft: 'none',
        borderRight: 'none',
        borderTop: 'none',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <Icon name="alert" />
      <div style={{ flex: '1 1 280px', minWidth: 0 }}>
        <div>
          Modo soporte (solo lectura) — estás viendo este panel como{' '}
          <strong>{superAdmin}</strong>.
        </div>
        <div style={{ fontSize: 11.5, marginTop: 2 }}>
          Los cambios están deshabilitados. La sesión expira en 15 minutos; al
          expirar, vuelve a abrirla desde el panel de administración.
        </div>
        {error && (
          <div style={{ fontSize: 11.5, marginTop: 4, color: 'var(--red-fg)' }}>
            {error}
          </div>
        )}
      </div>
      <button
        type="button"
        className="btn btn-sm"
        onClick={() => void stopImpersonation()}
        disabled={stopping}
        style={{ flexShrink: 0 }}
      >
        {stopping ? 'Saliendo…' : 'Salir del modo soporte'}
      </button>
    </div>
  );
}
